import { token } from '$lib/features/auth/authStore';
import { API_BASE_URL, api } from '$lib/utils/api';
import { clearNullishValues } from '$lib/utils/object';
import { createMutation, createQuery } from '@tanstack/svelte-query';
import { format } from 'date-fns';
import { get } from 'svelte/store';

export const unhandledMessagesForClientOptions = (clientId) => ({
  queryKey: ['clientUnhandledMessages', clientId],
  queryFn: () =>
    api().get(`v2/messages/clients/${clientId}/numberOfUnhandledMessages`),
});

export const useGetNumberOfUnhandledMessagesForClientQuery = (
  { clientId },
  options = {},
) =>
  createQuery({
    ...unhandledMessagesForClientOptions(clientId),
    ...options,
  });

export const unhandledMessagesForCompanyOptions = (
  { companyId },
  options = {},
) => ({
  queryKey: ['companyUnhandledMessages', companyId],
  queryFn: () =>
    api().get(`v2/messages/company/${companyId}/numberOfUnhandledMessages`),
  ...options,
});

export const useGetNumberOfUnhandledMessagesForCompanyQuery = (
  props,
  options = {},
) => createQuery(unhandledMessagesForCompanyOptions(props, options));

export const useGetAllClientsConversationsQuery = (
  { clientId },
  options = {},
) =>
  createQuery({
    queryKey: ['clientConversations', clientId],
    queryFn: () => api().get(`v2/messages/clients/${clientId}/conversations`),
    ...options,
  });

export const useGetAllCompanyConversationsQuery = (
  { companyId },
  options = {},
) =>
  createQuery({
    queryKey: ['companyConversations', companyId],
    queryFn: () => api().get(`v2/messages/company/${companyId}/conversations`),
    ...options,
  });
export const useCreateMainConversationMutation = (options = {}) =>
  createMutation({
    mutationFn: ({ clientId }) =>
      api().post(`v2/messages/clients/${clientId}/createMainConversation`),
    ...options,
  });

export const getConversationByIdQueryOptions = (
  { conversationId, companyConversation },
  options = {},
) => ({
  queryKey: ['clientConversation', conversationId, !!companyConversation],
  queryFn: () =>
    api().get(
      `v2/messages/conversations/${conversationId}${companyConversation ? '?companyConversation="true"' : ''}`,
    ),
  ...options,
});

export const useGetConversationByIdQuery = (...args) =>
  createQuery(getConversationByIdQueryOptions(...args));

export const getConversationSuggestionByIdQueryOptions = (
  { conversationId },
  options = {},
) => ({
  queryKey: ['clientConversationSuggestions', conversationId],
  queryFn: () =>
    api().get(`v2/messages/conversations/${conversationId}/suggestions`),
  ...options,
});

export const useGetConversationSuggestionByIdQuery = (...args) =>
  createQuery(getConversationSuggestionByIdQueryOptions(...args));

export const getClientMessageTemplatesQueryOptions = (
  { conversationId, language },
  options = {},
) => {
  return {
    queryKey: ['messageTemplates', conversationId, language],
    queryFn: () =>
      api().get(
        `v2/messages/conversations/${conversationId}/templates${
          language ? `?language=${language}` : ''
        }`,
      ),
    ...options,
  };
};

export const useGetClientMessageTemplatesQuery = (...args) =>
  createQuery(getClientMessageTemplatesQueryOptions(...args));

// --- Message-template management (#4041) — SupervisorNurse + Developer ------

export const getMessageTemplatesQueryOptions = (options = {}) => ({
  queryKey: ['messageTemplatesAdmin'],
  queryFn: () => api().get('v2/messages/templates'),
  ...options,
});

export const useGetMessageTemplatesQuery = (...args) =>
  createQuery(getMessageTemplatesQueryOptions(...args));

export const useCreateMessageTemplateMutation = (options = {}) =>
  createMutation({
    mutationFn: (template) => api().post('v2/messages/templates', template),
    ...options,
  });

export const useUpdateMessageTemplateMutation = (options = {}) =>
  createMutation({
    mutationFn: ({ id, ...template }) =>
      api().put(`v2/messages/templates/${id}`, template),
    ...options,
  });

export const useDeleteMessageTemplateMutation = (options = {}) =>
  createMutation({
    mutationFn: ({ id }) => api().delete(`v2/messages/templates/${id}`),
    ...options,
  });

export const clientMessageTemplatesForScheduledMessagesOptions = (
  userId,
  languageCode,
) => ({
  queryKey: ['messageTemplates', userId, languageCode],
  queryFn: () =>
    api().get(
      `v2/messages/scheduled/clientMessageTemplates?userId=${userId}&languageCode=${languageCode}`,
    ),
});

export const useGetClientMessageTemplatesForScheduledMessagesQuery = (
  { userId, languageCode },
  options = {},
) => {
  return createQuery({
    ...clientMessageTemplatesForScheduledMessagesOptions(userId, languageCode),
    ...options,
  });
};

export const useMarkSuggestionAsUsedMutation = (options = {}) =>
  createMutation({
    mutationKey: ['markSuggestionAsUsed'],
    mutationFn: ({ suggestionId }) =>
      api().post(`v2/messages/suggestions/${suggestionId}/markAsUsed`),
    ...options,
  });

export const useMarkAsReadMutation = (options = {}) =>
  createMutation({
    mutationKey: ['markAsRead'],
    mutationFn: ({ conversationId }) =>
      api().post(`v2/messages/conversations/${conversationId}/markAsHandled`),
    ...options,
  });

export const useSendMessageMutation = (options = {}) =>
  createMutation({
    mutationKey: ['sendMessage'],
    // `attachmentIds`: staged chat attachments (#5778); `message` may be
    // omitted for a file-only message (the API needs at least one of them).
    mutationFn: ({ conversationId, message, attachmentIds }) =>
      api().post(`v2/messages/conversations/${conversationId}/sendMessage`, {
        message,
        attachmentIds: attachmentIds?.length ? attachmentIds : undefined,
      }),
    ...options,
  });

export const useEditMessageMutation = (options = {}) =>
  createMutation({
    mutationKey: ['editMessage'],
    mutationFn: ({
      conversationId,
      messageId,
      text,
      removeAttachmentIds,
      addAttachmentIds,
    }) =>
      api().put(`v2/messages/conversations/${conversationId}/updateMessage`, {
        messageId,
        text,
        removeAttachmentIds: removeAttachmentIds?.length
          ? removeAttachmentIds
          : undefined,
        addAttachmentIds: addAttachmentIds?.length
          ? addAttachmentIds
          : undefined,
      }),
    ...options,
  });

const attachmentUrl = (conversationId, attachmentId) => {
  const base = new URL(
    `v2/messages/conversations/${conversationId}/attachments`,
    API_BASE_URL,
  );
  if (attachmentId !== undefined) base.pathname += `/${attachmentId}`;
  return base.toString();
};

const authHeaders = () => {
  const accessToken = get(token);
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

/**
 * @param {number} conversationId
 * @param {File} file
 */
export async function stageMessageAttachment(conversationId, file) {
  const body = new FormData();
  body.append('file', file);
  const response = await fetch(attachmentUrl(conversationId), {
    method: 'POST',
    headers: authHeaders(),
    body,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw { ...data, status: response.status };
  return data;
}

/**
 * Deletes a staged (unsent, own) attachment. `keepalive` lets the request
 * outlive a page unload.
 * @param {number} conversationId
 * @param {number} attachmentId
 * @param {{ keepalive?: boolean }} [options]
 */
export async function deleteStagedMessageAttachment(
  conversationId,
  attachmentId,
  { keepalive = false } = {},
) {
  const response = await fetch(attachmentUrl(conversationId, attachmentId), {
    method: 'DELETE',
    headers: authHeaders(),
    keepalive,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw { ...data, status: response.status };
  }
}

export const useRemoveMessageMutation = (options = {}) =>
  createMutation({
    mutationKey: ['removeMessage'],
    mutationFn: ({ conversationId, messageId }) =>
      api().delete(`v2/messages/conversations/${conversationId}/${messageId}`),
    ...options,
  });

export const useCreateCompanyConversationMutation = (options = {}) =>
  createMutation({
    mutationKey: ['sendMessage'],
    mutationFn: (formData) =>
      api().post(`v2/messages/company/conversation/create`, formData),
    ...options,
  });

export const useGetNumberOfUnhandledClientsQuery = () =>
  createQuery({
    queryKey: ['unhandledClients'],
    queryFn: () => api().get('v2/messages/monitored/count'),
    refetchInterval: 1000 * 60 * 1, // 1 minute,
  });

export const useGetNumberOfUnhandledCompanyMessages = () =>
  createQuery({
    queryKey: ['unhandledCompanyMessagesCount'],
    queryFn: () => api().get('v2/messages/company/count'),
    refetchInterval: 1000 * 60 * 1, // 1 minute,
  });

export const getUnhandledCompanyMessagesOptions = (props, options = {}) => ({
  queryKey: ['unhandledCompanyMessages', props],
  queryFn: () => {
    const setProps = {
      ...props,
      direction: props.direction?.toLowerCase(),
    };

    return api().get(`v2/messages/company`, setProps);
  },
  select: (data) => {
    // Good place to convert incoming data
    const updatedData = data.companyConversations.map((conversation) => {
      conversation.createdAt = format(
        new Date(conversation.createdAt),
        'yyyy-MM-dd HH:mm',
      );
      return conversation;
    });
    return {
      ...data,
      updatedData,
    };
  },
  ...options,
});

export const useGetUnhandledCompanyMessages = (...args) =>
  createQuery(getUnhandledCompanyMessagesOptions(...args));

export const getNotMonitoredClientsWithUnhandledMessagesQueryOptions = (
  props,
  options = {},
) => ({
  queryKey: ['notMonitoredClientsWithUnhandledMessages', props],
  queryFn: () => {
    const setProps = {
      ...props,
      direction: props.direction?.toLowerCase(),
    };

    return api().get(`v2/messages/monitored/nobody`, setProps);
  },
  select: (data) => {
    // Good place to convert incoming data
    const updatedData = data.clientsConversations.map((conversation) => {
      conversation.createdAt = format(
        new Date(conversation.createdAt),
        'yyyy-MM-dd HH:mm',
      );
      return conversation;
    });
    return {
      ...data,
      updatedData,
    };
  },
  ...options,
});

export const useGetNotMonitoredClientsWithUnhandledMessagesQuery = (...args) =>
  createQuery(getNotMonitoredClientsWithUnhandledMessagesQueryOptions(...args));

export const getMyClientsWithUnhandledMessagesQueryOptions = (
  props,
  options = {},
) => ({
  queryKey: ['monitoredByMeClientsWithUnhandledMessages', props],
  queryFn: () => {
    const setProps = {
      ...props,
      direction: props.direction?.toLowerCase(),
    };

    return api().get(`v2/messages/monitored/me`, setProps);
  },
  select: (data) => {
    // Good place to convert incoming data
    const updatedData = data.clientsConversations.map((conversation) => {
      conversation.createdAt = format(
        new Date(conversation.createdAt),
        'yyyy-MM-dd HH:mm',
      );
      return conversation;
    });
    return {
      ...data,
      updatedData,
    };
  },
  ...options,
});

export const useGetMyClientsWithUnhandledMessagesQuery = (...args) =>
  createQuery(getMyClientsWithUnhandledMessagesQueryOptions(...args));

export const getAllMonitoredClientsWithUnhandledMessagesQueryOptions = (
  props,
  options = {},
) => ({
  queryKey: ['monitoredByAnyClientsWithUnhandledMessages', props],
  queryFn: () => {
    const setProps = {
      ...props,
      direction: props.direction?.toLowerCase(),
    };

    return api().get(`v2/messages/monitored/any`, setProps);
  },
  select: (data) => {
    // Good place to convert incoming data
    const updatedData = data.clientsConversations.map((conversation) => {
      conversation.createdAt = format(
        new Date(conversation.createdAt),
        'yyyy-MM-dd HH:mm',
      );
      if (conversation.nurseId != null) {
        conversation.monitoredBy =
          conversation.nurseFirstName + ' ' + conversation.nurseLastName;
      } else {
        conversation.monitoredBy = 'N/A';
      }

      return conversation;
    });

    return {
      ...data,
      updatedData,
    };
  },
  ...options,
});

export const useGetAllMonitoredClientsWithUnhandledMessagesQuery = (...args) =>
  createQuery(getAllMonitoredClientsWithUnhandledMessagesQueryOptions(...args));

export const getScheduledMessagesList = (filter) => {
  const q = new URLSearchParams(clearNullishValues(filter));
  return api().get(`v2/messages/scheduled?${q.toString()}`);
};

export const createScheduledMessage = (data) =>
  api().post('v2/messages/scheduled', data);

export const updateScheduledMessage = ({ id, ...data }) =>
  api().put(`v2/messages/scheduled/${id}`, data);

export const deleteScheduledMessage = (data) =>
  api().delete('v2/messages/scheduled', data);

export const generateFollowUpMessage = ({ noteId }) =>
  api().post(`v2/messages/generate-follow-up/${noteId}`);

export const getAdminWelcomeMessageQuery = () => {
  return createQuery({
    queryKey: ['adminWelcomeMessage'],
    queryFn: () => api().get(`v2/messages/adminWelcomeMessage`),
  });
};

export const updateAdminWelcomeMessageMutation = (options = {}) => {
  return createMutation({
    mutationFn: ({ data }) =>
      api().put(`v2/messages/adminWelcomeMessage`, data),
    ...options,
  });
};
